/**
 * /learn/dashboard — learner home.
 *
 * Hero + Continue-learning carousel + bucket tabs. The hero subtitle doubles
 * as a points motivator when gamification is enabled.
 */

import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion, useReducedMotion } from 'framer-motion';
import { slideUp } from '@/lib/motion';
import { getDashboard } from '../../api/me';
import { useAuth } from '../../hooks/useAuth';
import { toDisplayBuckets } from '../../utils/courseBuckets';
import CourseCard from '../../components/learner/CourseCard';
import LearnerWelcomeBanner from '../../components/learner/LearnerWelcomeBanner';
import RecentEnrollmentPanel from '../../components/learner/RecentEnrollmentPanel';
import GamificationStack from '../../components/learner/dashboard/GamificationStack';

export const learnerKeys = {
  profile: ['me', 'profile'] as const,
  dashboard: ['me', 'dashboard'] as const,
  courses: ['me', 'courses'] as const,
  course: (assignmentId: string) => ['me', 'course', assignmentId] as const,
  attempt: (assignmentId: string, moduleId: string) =>
    ['me', 'course', assignmentId, 'module', moduleId, 'attempt'] as const,
  feedback: (assignmentId: string, moduleId: string) =>
    ['me', 'course', assignmentId, 'module', moduleId, 'feedback'] as const,
  points: ['me', 'gamification', 'points'] as const,
  programs: ['me', 'programs'] as const,
  certificates: ['me', 'certificates'] as const,
  config: ['me', 'config'] as const,
} as const;

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const reduceMotion = useReducedMotion();
  const { data, isLoading, isError } = useQuery({
    queryKey: learnerKeys.dashboard,
    queryFn: getDashboard,
  });

  // Reclassify expired in-progress courses into the finalized bucket so the
  // "Continue learning" carousel only surfaces enrolments the learner can
  // actually act on. Must be declared above the early returns below so hook
  // order stays stable across the loading → loaded transition.
  const displayBuckets = useMemo(
    () => (data ? toDisplayBuckets(data.courses) : null),
    [data]
  );

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="aspect-[16/9] sm:aspect-[21/8] rounded-[28px] bg-text-tertiary/10" />
        <div className="h-8 w-1/3 rounded bg-text-tertiary/10" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-44 rounded-2xl bg-text-tertiary/10" />
          ))}
        </div>
      </div>
    );
  }

  if (isError || !data || !displayBuckets) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <p className="text-text-secondary">Failed to load dashboard. Please refresh.</p>
      </div>
    );
  }

  const firstName = user?.first_name ?? user?.full_name?.split(' ')[0] ?? 'there';
  const fullName =
    user?.full_name ||
    [user?.first_name, user?.last_name].filter(Boolean).join(' ') ||
    firstName;
  const inProgress = displayBuckets.in_progress;

  return (
    <motion.div {...slideUp} className="space-y-8">
      {/* Welcome banner — purple radial-gradient + mascot */}
      <LearnerWelcomeBanner firstName={firstName} fullName={fullName} />

      {/* Recent enrollment panel — featured course on dark card */}
      <RecentEnrollmentPanel buckets={data.courses} />

      {/* Continue learning */}
      {inProgress.length > 0 && (
        <section className="space-y-3">
          <header className="flex items-end justify-between gap-3 px-1">
            <div>
              <h2 className="text-xl sm:text-2xl font-extrabold text-text-primary">
                Continue learning
              </h2>
              <p className="text-sm text-text-secondary mt-0.5">
                Jump back into the course you were last on.
              </p>
            </div>
          </header>

          {/* Horizontal-scroll on mobile, grid on desktop. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {inProgress.slice(0, 3).map((course, i) => (
              <motion.div
                key={course.assignment_id ?? course.course_id}
                initial={reduceMotion ? false : { opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.22, delay: reduceMotion ? 0 : i * 0.05 }}
              >
                <CourseCard course={course} />
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Gamification 3-stack: leaderboard / current score / prize reminder */}
      <GamificationStack />

      {data.certificates_count > 0 && (
        <div className="p-4 rounded-xl border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-950/30">
          <p className="text-sm font-medium text-primary-700 dark:text-primary-300">
            You have {data.certificates_count} certificate{data.certificates_count !== 1 ? 's' : ''}.&nbsp;
            <a href="/learn/certificates" className="underline">View all</a>
          </p>
        </div>
      )}
    </motion.div>
  );
};

export default DashboardPage;
